import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SampleDetails} from 'constants/SampleConstants';
import {BatchInfo, BioentityInfo} from 'folder-items/FileConstants';
import {
  RegistrationTable,
  RegistrationTableType,
} from 'notebook/registration-tables/RegistrationTableConstants';
import {LegacySamplesSource} from 'notebook/sample-propagation/legacy/legacy-add-samples-modal/LegacyAddSamplesModal';
import makeEmptySampleDetails from 'notebook/sample-propagation/util/makeEmptySampleDetails';
import {getColumnDefinitionIndexForSample} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {BatchTypedValue, BioentityTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';

/**
 * This file contains "Ops" -- public functions for interacting with registration tables.
 * The similar "Util" file is for internal consumption only, used within the
 * RegistrationTable subsystem.
 */

export function getRegistrationTableSampleSources(
  tables: Array<RegistrationTable>
): Array<LegacySamplesSource> {
  return tables.map((t) => ({
    name: t.name,
    icon: ModelTypeConstants.REGISTRY,
    sampleDetails: getSampleDetailsFromRegistrationTable(t),
  }));
}

export function getSampleDetailsFromRegistrationTable(table: RegistrationTable): Array<SampleDetails> {
  const finalizedRows = table.rows.filter((row) => row.isFinalized);
  if (finalizedRows.length === 0 || !table.tagSchema) {
    return [];
  }

  let batchColIndex: number | null = null;
  let fileColIndex: number | null = null;

  if (table.tableType === RegistrationTableType.BATCH) {
    batchColIndex = getColumnDefinitionIndexForSample(table.columns, TableColumnType.BATCH);
    assert(batchColIndex !== -1, 'Batch table type must have a batch column index.');
  } else {
    fileColIndex = getColumnDefinitionIndexForSample(table.columns, TableColumnType.EXISTING_BIOENTITY);
    assert(fileColIndex !== -1, 'Bioentity table type must have a bioentity column index.');
  }

  return finalizedRows.map((row) => {
    let batchInfo: BatchInfo | null = null;
    let bioentityInfo: BioentityInfo | null = null;

    if (table.tableType === RegistrationTableType.BATCH) {
      const typedValue = row.cells[batchColIndex!].typedValue as BatchTypedValue;
      if (typedValue != null) {
        assert(
          typedValue.type === BaseTypedValueType.BATCH,
          'RegistrationTable cell in the Batch column does not have the Batch type.'
        );
        batchInfo = typedValue.batchInfo;
      }
    } else {
      const typedValue = row.cells[fileColIndex!].typedValue as BioentityTypedValue;
      if (typedValue != null) {
        assert(
          typedValue.type === BaseTypedValueType.BIOENTITY,
          'RegistrationTable cell in the Bioentity column does not have the Bioentity type'
        );
        bioentityInfo = typedValue.bioentitySummary;
      }
    }

    return {
      ...makeEmptySampleDetails(),
      bioentityInfo,
      batchInfo,
    };
  });
}
